import React, { useEffect } from 'react';
import {
  FlexGrid,
  getClassNameFactory,
  getRehydratableName,
  Heading,
  KalturaVideoPlayer,
  TextBoldness,
  Typography,
  VerticalSpacing,
  VerticalSpacingSizes,
} from '@dcl/ui';
import { types, boldTypes } from '@dcl/ui/dist/elements/Heading';
import { propsToDataAttributes } from '@tr-digital/react-rehydrate';
import { IPlaylistVideoPlayer } from '../../types';
import { kalturaSetupHandlerThunk } from '@dcl/ui/dist/fragments';
import { CourseIcon } from '../../../../icons/CourseIcon';
import { VideoIcon } from './icons/VideoIcon';
import { convertSecondsIntoMins } from '../../../../../../utils/convertSecondsIntoMins';
import { FORMAT, VIEWS } from '../../constants';
import './PlaylistVideoPlayer.css';
import { TypographyTags } from '@dcl/ui/dist/elements/Typography';

export const PlaylistVideoPlayer: React.FC<IPlaylistVideoPlayer> = (props) => {
  const getClassName = getClassNameFactory(PlaylistVideoPlayer.displayName);
  const {
    title,
    entryId,
    userKeyKs,
    duration = '0',
    views,
    format = 'Video',
  } = props;

  useEffect(() => {
    kalturaSetupHandlerThunk();
  }, [entryId]);

  return (
    <div
      className={getClassName()}
      data-rehydratable={getRehydratableName(PlaylistVideoPlayer.displayName)}
      {...propsToDataAttributes(props)}
      role="tabpanel"
      aria-labelledby={entryId}
    >
      <div
        className={getClassName({
          descendantName: 'player',
        })}
      >
        <KalturaVideoPlayer entryId={entryId} ks={userKeyKs} />
      </div>

      <div
        className={getClassName({
          descendantName: 'description',
        })}
      >
        <Heading
          type={types.Small}
          boldType={boldTypes.medium}
          data-testid="test-title"
          className={getClassName({
            descendantName: 'title',
          })}
        >
          <div title={title}>{title}</div>
        </Heading>
        <VerticalSpacing size={VerticalSpacingSizes.S}>
          <FlexGrid>
            <FlexGrid.Row
              className={getClassName({ descendantName: 'eyeBrow' })}
            >
              <FlexGrid.Col xs={3} sm={3} md={3}>
                <Typography
                  tag={TypographyTags.SPAN}
                  boldness={TextBoldness.Regular}
                  className={getClassName({
                    descendantName: 'articleFormat',
                  })}
                >
                  {FORMAT}
                </Typography>
              </FlexGrid.Col>

              <FlexGrid.Col xs={9} sm={9} md={9}>
                <div className={getClassName({ descendantName: 'Info' })}>
                  <div className={getClassName({ descendantName: 'Icon' })}>
                    {format.toLocaleLowerCase() === 'course' ? (
                      <CourseIcon />
                    ) : (
                      <VideoIcon />
                    )}
                  </div>
                  <Typography
                    tag={TypographyTags.SPAN}
                    boldness={TextBoldness.Light}
                    className={getClassName({
                      descendantName: 'articleFormat',
                    })}
                  >
                    {format}
                  </Typography>
                  <Typography
                    tag={TypographyTags.SPAN}
                    boldness={TextBoldness.Regular}
                  >
                    {convertSecondsIntoMins(duration)}
                  </Typography>
                  <Typography
                    tag={TypographyTags.SPAN}
                    boldness={TextBoldness.Regular}
                  >
                    ·
                  </Typography>
                  <Typography
                    tag={TypographyTags.SPAN}
                    boldness={TextBoldness.Regular}
                  >
                    {views} {VIEWS}
                  </Typography>
                </div>
              </FlexGrid.Col>
            </FlexGrid.Row>
          </FlexGrid>
        </VerticalSpacing>
      </div>
    </div>
  );
};

PlaylistVideoPlayer.displayName = 'PlaylistVideoPlayer';
